import { locales } from '@/routing'
import { findOption } from '@/shared/utils/typesUtils'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocale, useTranslations } from 'next-intl'
import { isNil } from 'lodash'

import { useUpdateCompany } from '@/shared/hooks/useCompanies'
import { useDialogState } from '@/shared/hooks/useDialogState'
import type { Company } from '@/shared/models/companyModel'
import { inputMessageContext, useInputRequiredContext, inputStateContext } from '@/shared/utils/inputUtils'
import { DrawerClose, DrawerContent, DrawerRoot, DrawerTrigger } from '@/shared/v2/shared/components/Drawer/Drawer'
import Button from '@/shared/v2/shared/components/Button'
import LoadingButton from '@/shared/v2/shared/components/LoadingButton'
import Input from '@/shared/v2/shared/components/Input'
import Select from '@/shared/v2/shared/components/Select'
import { useCountries } from '@/shared/hooks/useCountry'
import type { Identifier } from '@/shared/models/debtorModels'
import PrivilegesButton from '@/shared/v2/shared/components/PrivilegesButton'

type UpdateProfileCompanyForm = {
    address: Company['address'] | undefined
}

const createDataTestId = (id: string) => `__update-settings-address-company-${id}__`

const mapDefaultValues = (company: Company | undefined): UpdateProfileCompanyForm => ({
    address: company?.address
})

export type UpdateSettingsAddressCompanyProps = {
    company: Company | undefined
}

const UpdateSettingsAddressCompany = ({ company }: UpdateSettingsAddressCompanyProps) => {
    const locale = useLocale()
    const settingsTranslations = useTranslations('settings')

    const updateCompany = useUpdateCompany()
    const defaultValues = useMemo(() => mapDefaultValues(company), [company])
    const { isOpen, onOpenChange } = useDialogState({
        shouldCloseIf: updateCompany.isSuccess
    })

    const countries = useCountries(findOption(locales, locale))
    const mappedCountries = countries.data?.map((country) => ({
        value: country.country,
        label: country.label
    }))

    const resolveCountryValue = (value: Identifier['country'] | undefined | null) => {
        if (isNil(value)) return null

        const item = countries.data?.find((item) => item.country === value)

        return {
            value: item?.country,
            label: item?.label
        }
    }

    const {
        formState: { isDirty, errors },
        control,
        watch,
        handleSubmit,
        register,
        reset
    } = useForm<UpdateProfileCompanyForm>({
        defaultValues
    })

    useEffect(() => {
        if (!isDirty) {
            reset(defaultValues)
        }
    }, [defaultValues, isDirty, reset])

    const getInputState = inputStateContext<UpdateProfileCompanyForm>(watch, errors)
    const getInputMessage = inputMessageContext<UpdateProfileCompanyForm>(errors)
    const getRequiredValue = useInputRequiredContext()

    const onSubmit = (payload: UpdateProfileCompanyForm) => {
        updateCompany.mutate(payload)
    }

    return (
        <DrawerRoot open={isOpen} onOpenChange={onOpenChange}>
            <DrawerTrigger asChild>
                <PrivilegesButton
                    requiredPrivilege="manager"
                    data-testid={createDataTestId('trigger')}
                    intent="secondary"
                >
                    {settingsTranslations('actions.edit')}
                </PrivilegesButton>
            </DrawerTrigger>
            <DrawerContent title={settingsTranslations('company.addressTitle')}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 gap-y-5">
                        <Input
                            data-testid={createDataTestId('first')}
                            state={getInputState('address.first')}
                            message={getInputMessage('address.first')}
                            placeholder={settingsTranslations('company.update.inputs.first.placeholder')}
                            label={settingsTranslations('company.update.inputs.first.label')}
                            {...register('address.first', {
                                required: getRequiredValue()
                            })}
                        />
                        <Input
                            data-testid={createDataTestId('city')}
                            state={getInputState('address.city')}
                            message={getInputMessage('address.city')}
                            placeholder={settingsTranslations('company.update.inputs.city.placeholder')}
                            label={settingsTranslations('company.update.inputs.city.label')}
                            {...register('address.city', {
                                required: getRequiredValue()
                            })}
                        />
                        <Input
                            data-testid={createDataTestId('postal')}
                            state={getInputState('address.postal')}
                            message={getInputMessage('address.postal')}
                            placeholder={settingsTranslations('company.update.inputs.postal.placeholder')}
                            label={settingsTranslations('company.update.inputs.postal.label')}
                            {...register('address.postal', {
                                required: getRequiredValue()
                            })}
                        />
                        <Controller
                            control={control}
                            name="address.country"
                            rules={{
                                required: getRequiredValue()
                            }}
                            render={({ field: { onChange, value } }) => (
                                <Select
                                    data-testid={createDataTestId('country')}
                                    placeholder={settingsTranslations('company.update.inputs.country.placeholder')}
                                    label={settingsTranslations('company.update.inputs.country.label')}
                                    onChange={(option) => onChange(option?.value)}
                                    message={getInputMessage('address.country')}
                                    value={resolveCountryValue(value)}
                                    state={getInputState('address.country')}
                                    options={mappedCountries}
                                    isLoading={countries.isLoading}
                                    required
                                />
                            )}
                        />

                        <div className="grid grid-cols-2 gap-3 w-full">
                            <DrawerClose asChild>
                                <Button data-testid={createDataTestId('cancel')} intent="secondary">
                                    {settingsTranslations('actions.cancel')}
                                </Button>
                            </DrawerClose>
                            <LoadingButton
                                data-testid={createDataTestId('submit')}
                                isLoading={updateCompany.isPending}
                                type="submit"
                            >
                                {settingsTranslations('actions.save')}
                            </LoadingButton>
                        </div>
                    </div>
                </form>
            </DrawerContent>
        </DrawerRoot>
    )
}

export default UpdateSettingsAddressCompany
